import * as React from "react";
import { Button, Eyebrow } from "./primitives";
import { IconCheck, IconX } from "./icons";

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------
export function Container({
  children,
  small,
  className = "",
}: {
  children: React.ReactNode;
  small?: boolean;
  className?: string;
}) {
  return (
    <div className={[small ? "container-sm" : "container", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionHead
// ---------------------------------------------------------------------------
export interface SectionHeadProps {
  kicker?: React.ReactNode;
  title: React.ReactNode;
  lede?: React.ReactNode;
  brand?: boolean;
  align?: "left" | "center";
  className?: string;
}
export function SectionHead({
  kicker,
  title,
  lede,
  brand,
  align = "left",
  className = "",
}: SectionHeadProps) {
  return (
    <div className={["section-head", `align-${align}`, className].filter(Boolean).join(" ")}>
      {kicker && (
        <Eyebrow brand={brand} dot={brand}>
          {kicker}
        </Eyebrow>
      )}
      {/* Rendered as <h2> for a valid single-<h1> heading outline; the .h1 class
          preserves the existing visual size. The page hero owns the sole <h1>. */}
      <h2 className="h1">{title}</h2>
      {lede && <p className="lede">{lede}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompareGrid
// ---------------------------------------------------------------------------
export interface CompareItem {
  title: React.ReactNode;
  desc: React.ReactNode;
}
export interface CompareGridProps {
  good: { label: React.ReactNode; title: React.ReactNode; items: CompareItem[] };
  bad: { label: React.ReactNode; title: React.ReactNode; items: CompareItem[] };
  className?: string;
}
export function CompareGrid({ good, bad, className = "" }: CompareGridProps) {
  return (
    <div className={["compare-grid", className].filter(Boolean).join(" ")}>
      <div className="comp good">
        <h3>
          <span className="lbl">{good.label}</span> {good.title}
        </h3>
        <ul className="ticklist good">
          {good.items.map((it, i) => (
            <li key={i}>
              <span className="ic">
                <IconCheck />
              </span>
              <div>
                <b>{it.title}</b>
                <span>{it.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="comp bad">
        <h3>
          <span className="lbl">{bad.label}</span> {bad.title}
        </h3>
        <ul className="ticklist bad">
          {bad.items.map((it, i) => (
            <li key={i}>
              <span className="ic">
                <IconX />
              </span>
              <div>
                <b>{it.title}</b>
                <span>{it.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductCard
// ---------------------------------------------------------------------------
export interface ProductCardProps {
  tag: React.ReactNode;
  title: React.ReactNode;
  lede: React.ReactNode;
  proof?: React.ReactNode;
  features: { title: React.ReactNode; desc: React.ReactNode }[];
  cta?: { label: React.ReactNode; href: string; variant?: "primary" | "brand" };
  className?: string;
}
export function ProductCard({
  tag,
  title,
  lede,
  proof,
  features,
  cta,
  className = "",
}: ProductCardProps) {
  return (
    <article className={["prod", className].filter(Boolean).join(" ")}>
      <span className="tag">{tag}</span>
      <h2>{title}</h2>
      <p className="prod-lede">{lede}</p>
      {proof && <div className="proof">{proof}</div>}
      <ul className="feats">
        {features.map((f, i) => (
          <li key={i}>
            <b>{f.title}</b>
            <span>{f.desc}</span>
          </li>
        ))}
      </ul>
      {cta && (
        <Button as="a" href={cta.href} variant={cta.variant || "primary"} size="md">
          {cta.label}
        </Button>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// DisclaimerCard
// ---------------------------------------------------------------------------
export interface DisclaimerCardProps {
  icon?: React.ReactNode;
  kicker?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
export function DisclaimerCard({
  icon,
  kicker = "Important to know",
  children,
  className = "",
}: DisclaimerCardProps) {
  return (
    <div className={["disc-card", className].filter(Boolean).join(" ")}>
      <div className="kicker">
        {icon}
        {kicker}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper — numbered steps for how-it-works sections (renders .how-grid)
// ---------------------------------------------------------------------------
export interface StepperItem {
  title: React.ReactNode;
  desc: React.ReactNode;
}
export interface StepperProps {
  steps: StepperItem[];
  className?: string;
}
export function Stepper({ steps, className = "" }: StepperProps) {
  return (
    <div className={["how-grid", className].filter(Boolean).join(" ")}>
      {steps.map((s, i) => (
        <div className="step" key={i}>
          <div className="num">{i + 1}</div>
          <h3>{s.title}</h3>
          <p>{s.desc}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReliabilityGrid — dark cards on dark section (renders .baddays-grid)
// ---------------------------------------------------------------------------
export interface ReliabilityItem {
  icon?: React.ReactNode;
  title: React.ReactNode;
  desc: React.ReactNode;
}
export interface ReliabilityGridProps {
  items: ReliabilityItem[];
  className?: string;
}
export function ReliabilityGrid({ items, className = "" }: ReliabilityGridProps) {
  return (
    <div className={["baddays-grid", className].filter(Boolean).join(" ")}>
      {items.map((it, i) => (
        <div className="bd-card" key={i}>
          {it.icon && <div className="ic">{it.icon}</div>}
          <h3>{it.title}</h3>
          <p>{it.desc}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PriceLockup — inline price + caption row used in product heroes
// ---------------------------------------------------------------------------
export interface PriceLockupProps {
  amount: React.ReactNode;
  period?: React.ReactNode;
  caption?: React.ReactNode;
  className?: string;
}
export function PriceLockup({ amount, period, caption, className = "" }: PriceLockupProps) {
  return (
    <div className={["price-row", className].filter(Boolean).join(" ")}>
      <b>
        {amount}
        {period}
      </b>
      {caption && <span>{caption}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTABand
// ---------------------------------------------------------------------------
export interface CTABandProps {
  title: React.ReactNode;
  lede?: React.ReactNode;
  ctas: { label: React.ReactNode; href: string; variant?: "primary" | "brand" | "secondary" }[];
  className?: string;
}
export function CTABand({ title, lede, ctas, className = "" }: CTABandProps) {
  return (
    <section className={["cta-band", className].filter(Boolean).join(" ")}>
      <div>
        <h2>{title}</h2>
        {lede && <p>{lede}</p>}
      </div>
      <div className="ctas">
        {ctas.map((c, i) => (
          <Button as="a" href={c.href} variant={c.variant || "primary"} size="lg" key={i}>
            {c.label}
          </Button>
        ))}
      </div>
    </section>
  );
}
